import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Droplet, Settings as SettingsIcon, ChevronRight } from 'lucide-react';
import InletSensorSetup from './InletSensorSetup';
import OutletSensorSetup from './OutletSensorSetup';
import Outlet2SensorSetup from './Outlet2SensorSetup';
import SensorConfiguration from './SensorConfiguration';

// Channel colours as the panel sets them: inlet green, outlet blue,
// outlet 2 teal.
const INLET_COLOR = '#5cb85c';
const OUTLET_COLOR = '#428bca';
const OUTLET2_COLOR = '#17a2b8';

const MoistureSetup = ({ settingsData, getSettingValue, handleEdit, onBack }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  // A second outlet sensor is optional. The device folds this flag into the
  // settings stream alongside the settings themselves.
  const outlet2Setting = getSettingValue('outlet_2_enabled');
  const outlet2Enabled =
    outlet2Setting.value === 1 || outlet2Setting.value === '1' || outlet2Setting.value === true;

  const sensors = [
    {
      id: 'inlet',
      title: 'Inlet sensor',
      description: 'Configure inlet moisture sensor',
      component: InletSensorSetup,
      color: INLET_COLOR,
    },
    {
      id: 'outlet',
      title: 'Outlet sensor',
      description: 'Configure outlet moisture sensor',
      component: OutletSensorSetup,
      color: OUTLET_COLOR,
    },
    ...(outlet2Enabled
      ? [{
          id: 'outlet2',
          title: 'Outlet 2 sensor',
          description: 'Configure outlet 2 moisture sensor',
          component: Outlet2SensorSetup,
          color: OUTLET2_COLOR,
        }]
      : []),
  ];

  // The panel keeps this off the sensor grid and below a divider, because it
  // configures the sensors as a set rather than being one of them.
  const additionalOptions = {
    id: 'configuration',
    title: 'Additional options',
    component: SensorConfiguration,
  };

  const handleBack = () => {
    if (selectedOption) {
      setSelectedOption(null);
    } else {
      onBack();
    }
  };

  if (selectedOption) {
    const SelectedComponent = selectedOption.component;
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <h3 className="text-xl font-bold text-gray-800 capitalize">{selectedOption.title}</h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          <SelectedComponent
            settingsData={settingsData}
            getSettingValue={getSettingValue}
            handleEdit={handleEdit}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
        </button>
        <h3 className="text-xl font-bold text-gray-800">Moisture Sensor Setup</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sensors.map((sensor) => (
              <motion.button
                key={sensor.id}
                onClick={() => setSelectedOption(sensor)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.99 }}
                className="relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden text-left flex items-center gap-4 p-5"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${sensor.color}1a` }}
                >
                  <Droplet className="w-6 h-6" style={{ color: sensor.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-gray-800 capitalize">{sensor.title}</p>
                  <p className="text-sm text-gray-500">{sensor.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: sensor.color }} />
              </motion.button>
            ))}
          </div>

          <div className="border-t border-gray-200 my-6" />

          <motion.button
            onClick={() => setSelectedOption(additionalOptions)}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.99 }}
            className="w-full bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-left flex items-center gap-4 p-5"
          >
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              <SettingsIcon className="w-6 h-6 text-gray-500" />
            </div>
            <p className="flex-1 text-lg font-semibold text-gray-800 capitalize">
              {additionalOptions.title}
            </p>
            <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default MoistureSetup;
